'use client';

import { c as _c } from "react/compiler-runtime";
import { boot, onHide, onShow, show, shutdown, update } from '@intercom/messenger-js-sdk';
import { useActiveWallet, useAuth } from '@morfi/backend-sdk-v2/client';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { env } from '@/lib/config/env';
import { translateToast } from '@/lib/i18n/toast-translator';
import { stripDemoModePath } from '@/lib/network';
import { loadMessenger } from '@/lib/support/load-messenger';
import { logger } from '@/lib/telemetry/logger';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { openSupport, useSupportStore } from '@/stores/support';
type MessengerSettings = Parameters<typeof boot>[0];
const LG_BREAKPOINT = 1024;

/** Identity stays current locally; vendor code only starts after an explicit Support click. */
export function IntercomProvider() {
  const $ = _c(9);
  const {
    user,
    isAuthenticated
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const pathname = stripDemoModePath(usePathname());
  const hideLauncher = pathname === "/workspace/pattern" || pathname.startsWith("/workspace/pattern/");
  const request = useSupportStore(_temp);
  const settingsRef = useRef(null);
  const bootedRef = useRef(false);
  const visibleRef = useRef(false);
  const openingTimer = useRef(null);
  let t0;
  let t1;
  if ($[0] !== activeWallet || $[1] !== hideLauncher || $[2] !== isAuthenticated || $[3] !== user) {
    t0 = () => {
      const appId = env.NEXT_PUBLIC_INTERCOM_APP_ID;
      if (appId === undefined || appId === "") {
        return;
      }
      const configuredAppId = appId;
      const syncSettings = function syncSettings() {
        const previousUser = settingsRef.current?.user_id;
        const settings = {
          app_id: configuredAppId,
          hide_default_launcher: hideLauncher || window.innerWidth < LG_BREAKPOINT
        };
        if (isAuthenticated && user !== null) {
          settings.user_id = user.id;
          settings.created_at = Math.floor(new Date(user.createdAt).getTime() / 1000);
          if (user.email !== "") {
            settings.email = user.email;
          }
          if (user.displayName !== null) {
            settings.name = user.displayName;
          }
          if (activeWallet !== null) {
            settings.wallet_address = activeWallet.address;
          }
        }
        settingsRef.current = settings;
        if (!bootedRef.current) {
          return;
        }
        if (previousUser !== settings.user_id) {
          shutdown();
          visibleRef.current = false;
          boot(settings);
        } else {
          update(settings);
        }
      };
      syncSettings();
      const media = window.matchMedia(`(min-width: ${String(LG_BREAKPOINT)}px)`);
      media.addEventListener("change", syncSettings);
      return () => media.removeEventListener("change", syncSettings);
    };
    t1 = [isAuthenticated, user, activeWallet, hideLauncher];
    $[0] = activeWallet;
    $[1] = hideLauncher;
    $[2] = isAuthenticated;
    $[3] = user;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[6] !== request) {
    t2 = () => {
      if (request === 0) {
        return;
      }
      let cancelled = false;
      const opened = function opened() {
        visibleRef.current = true;
        if (openingTimer.current !== null) {
          clearTimeout(openingTimer.current);
        }
        useSupportStore.setState({
          status: "ready"
        });
        dismissToast(TOAST_IDS.SUPPORT_OPEN);
      };
      const open = async function open() {
        const appId_0 = env.NEXT_PUBLIC_INTERCOM_APP_ID;
        if (appId_0 === undefined || appId_0 === "") {
          logger.warn("support.messenger_open_failed", {
            reason: "not_configured"
          });
          showOpenFailure();
          return;
        }
        showToast({
          id: TOAST_IDS.SUPPORT_OPEN,
          message: translateToast("support.loading"),
          variant: "loading",
          duration: Number.POSITIVE_INFINITY
        });
        try {
          await loadMessenger(appId_0);
          if (cancelled) {
            return;
          }
          const settings_0 = settingsRef.current;
          if (settings_0 === null) {
            return;
          }
          if (!bootedRef.current) {
            boot(settings_0);
            onShow(opened);
            onHide(() => {
              visibleRef.current = false;
            });
            bootedRef.current = true;
          }
          if (visibleRef.current) {
            opened();
            return;
          }
          openingTimer.current = setTimeout(_temp2, 20000);
          show();
        } catch {
          if (cancelled) {
            return;
          }
          logger.warn("support.messenger_open_failed", {
            reason: "script_load_failed"
          });
          showOpenFailure();
        }
      };
      open();
      return () => {
        cancelled = true;
        if (openingTimer.current !== null) {
          clearTimeout(openingTimer.current);
        }
      };
    };
    t3 = [request];
    $[6] = request;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  return null;
}
function _temp2() {
  logger.warn("support.messenger_open_failed", {
    reason: "open_timeout"
  });
  showOpenFailure();
}
function _temp(state) {
  return state.request;
}
function showOpenFailure(): void {
  useSupportStore.setState({
    status: 'error'
  });
  showToast({
    id: TOAST_IDS.SUPPORT_OPEN,
    message: translateToast('support.load_failed'),
    variant: 'error',
    action: {
      label: translateToast('errorBoundary.retry'),
      onClick: openSupport
    }
  });
}